import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { ErrorBoundary } from "@sentry/react";
import { routeTree } from "./routeTree.gen";
import { AuthContextProvider } from "#/contexts/AuthContext";
import { MeContextProvider, type MeData } from "#/contexts/MeContext";
import { TooltipProvider } from "#/components/atoms/tooltip";
import {
  keycloakClient,
  initKeycloak,
  callKeycloakLoginEndpoint,
} from "#/utils/keycloak";
import { setKeycloak, apiClient } from "#/lib/api/client";
import { initSentry } from "#/utils/sentry";

import "./styles.css";

initSentry();

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false },
  },
});

const App = ({ meData }: { meData: MeData }) => {
  return (
    <ErrorBoundary>
      <AuthContextProvider value={keycloakClient}>
        <MeContextProvider meData={meData}>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <Toaster />
              <RouterProvider router={router} />
            </TooltipProvider>
          </QueryClientProvider>
        </MeContextProvider>
      </AuthContextProvider>
    </ErrorBoundary>
  );
};

const boot = async () => {
  const authenticated = await initKeycloak();
  if (!authenticated) return;

  // Give the API client access to the Keycloak instance for token reads
  setKeycloak(keycloakClient);

  await callKeycloakLoginEndpoint();

  const { data, error } = await apiClient.GET("/api/me");
  if (error !== undefined) {
    throw new Error("/me request failed");
  }

  const meData: MeData = {
    tenants: data.tenants,
    permissions: data.permissions,
    isAdmin: data.isAdmin,
  };

  const root = ReactDOM.createRoot(document.getElementById("app")!);
  root.render(<App meData={meData} />);
};

void boot();
